import {
	IntegrationFlowAuthenticationKind,
	IntegrationFlowAuthenticationIsRequiredModel
} from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";
import { useIntegrationFlowAuthenticationRequiredDialog } from "client/Features/Settings/IntegrationBuilder/IntegrationFlowAuthenticationRequiredDialog";
import { useAfasAuthenticate } from "client/Features/Settings/IntegrationBuilder/useAfasAuthenticate";
import { LinkKind, useLinkToIntegrationCredentialsDialog } from "client/Features/Settings/IntegrationCredentials/LinkToIntegrationCredentialsDialog";
import { never } from "client_legacy/ndaw/errors";
import { navigateToUriInPopOutWindow } from "client_legacy/ndaw/navigator";
import { ReactElement, useCallback } from "react";

export type AuthenticateIntegrationFlowResultKind = "completed" | "wasInvoked";

export type AuthenticateIntegrationFlowResult = [
	dialogElements: ReactElement | null,
	authenticate: (
		integrationName: string | undefined,
		missingAuthentications: readonly IntegrationFlowAuthenticationIsRequiredModel[],
		onIsBusyChange?: (isBusy: boolean) => void
	) => Promise<TryResult<AuthenticateIntegrationFlowResultKind>>
];

export function useTryAuthenticateIntegrationFlow(): AuthenticateIntegrationFlowResult {
	const [linkToCredentialsDialog, openLinkToCredentialsDialog] = useLinkToIntegrationCredentialsDialog();

	const [
		integrationFlowAuthenticationRequiredDialog,
		openIntegrationFlowAuthenticationRequiredDialog
	] = useIntegrationFlowAuthenticationRequiredDialog();

	const {
		authenticate: afasAuthenticate,
		afasOtpDialog
	} = useAfasAuthenticate();

	const authenticate = useCallback(
		async (
			integrationName: string | undefined,
			missingAuthentications: readonly IntegrationFlowAuthenticationIsRequiredModel[],
			onIsBusyChange?: (isBusy: boolean) => void
		): Promise<TryResult<AuthenticateIntegrationFlowResultKind>> => {
			if (missingAuthentications.length === 0) {
				return {
					wasSuccessful: true,
					result: "completed"
				};
			}

			if (missingAuthentications.length === 1) {
				const authentication: IntegrationFlowAuthenticationIsRequiredModel = missingAuthentications[0];

				switch (authentication.kind) {
					case IntegrationFlowAuthenticationKind.OAuth2:
						navigateToUriInPopOutWindow(authentication.authenticationUrl);

						return {
							wasSuccessful: true,
							result: "wasInvoked"
						};
					case IntegrationFlowAuthenticationKind.Basic:
						const result: TryResult<void> = await openLinkToCredentialsDialog({
							thingToLink: {
								kind: LinkKind.IntegrationFlowAuthentication,
								id: authentication.id
							}
						});

						return result.wasSuccessful
							? {
								wasSuccessful: true,
								result: "completed"
							}
							: { wasSuccessful: false };
					case IntegrationFlowAuthenticationKind.Afas:
						const didSucceed: boolean = await afasAuthenticate(
							authentication.id,
							onIsBusyChange
						);

						return didSucceed
							? {
								wasSuccessful: true,
								result: "completed"
							}
							: { wasSuccessful: false };
					default:
						never(authentication);
				}
			}

			const result: TryResult<void> = await openIntegrationFlowAuthenticationRequiredDialog({
				integrationName,
				missingAuthentications
			});

			return result.wasSuccessful
				? {
					wasSuccessful: true,
					result: "completed"
				}
				: { wasSuccessful: false };

		},
		[
			openLinkToCredentialsDialog,
			afasAuthenticate,
			openIntegrationFlowAuthenticationRequiredDialog
		]
	);

	return [
		integrationFlowAuthenticationRequiredDialog
			?? linkToCredentialsDialog
			?? afasOtpDialog,
		authenticate
	];
}